// useGraphFilter — pure, memoised graph filtering for the org chart.
//
// The hook is consumed by US6's <OrgGraph> and returns a stable reference
// whenever (graph, params) are unchanged so that downstream hooks such as
// useForceSimulation do not restart their simulation needlessly.
//
// Modes:
//   'all'         → returns the input graph verbatim.
//   'my-team'     → walks the manager chain upward AND collects all
//                   descendants of the current user. Peers are excluded.
//                   When currentUserUpn is null, falls back to the full
//                   graph with fallbackReason='no-current-user'.
//   'department'  → keeps nodes whose departmentId matches; edges survive
//                   only when both endpoints are still present.
//
// See specs/entra-org-chart/us5-filters.md.
import { useMemo } from 'react';
import type {
  IOrgGraphResponse,
  IOrgGraphNode,
  IOrgGraphEdge,
  IOrgGraphDepartment,
} from '@skystone/shared';

/** Safety cap for manager-chain walks. Real Entra data should never approach this. */
export const MANAGER_CHAIN_CAP = 50;

export type FilterParams =
  | { mode: 'all' }
  | { mode: 'my-team'; currentUserUpn: string | null }
  | { mode: 'department'; departmentId: string };

export type FilterFallbackReason = 'no-current-user' | null;

export interface FilteredGraph {
  nodes: IOrgGraphNode[];
  edges: IOrgGraphEdge[];
  departments: IOrgGraphDepartment[];
  fallbackReason: FilterFallbackReason;
}

interface NodeIndex {
  byUpn: Map<string, IOrgGraphNode>;
  reportsByManagerUpn: Map<string, IOrgGraphNode[]>;
}

function buildIndex(nodes: IOrgGraphNode[]): NodeIndex {
  const byUpn = new Map<string, IOrgGraphNode>();
  const reportsByManagerUpn = new Map<string, IOrgGraphNode[]>();
  for (const node of nodes) {
    byUpn.set(node.id, node);
    if (node.managerUpn !== null) {
      const existing = reportsByManagerUpn.get(node.managerUpn);
      if (existing) {
        existing.push(node);
      } else {
        reportsByManagerUpn.set(node.managerUpn, [node]);
      }
    }
  }
  return { byUpn, reportsByManagerUpn };
}

/**
 * Walks the manager chain upward from `startUpn`, capping at MANAGER_CHAIN_CAP
 * hops. Adds every encountered ancestor to `collected`. Logs a single warning
 * via `onCycleDetected` when the cap is hit or a cycle is observed.
 *
 * The caller is responsible for adding `startUpn` to `collected` before
 * invoking this helper.
 */
function walkAncestors(
  startUpn: string,
  index: NodeIndex,
  collected: Set<string>,
  onCycleDetected: () => void,
): void {
  const visited = new Set<string>([startUpn]);
  const startNode = index.byUpn.get(startUpn);
  if (startNode === undefined) {
    return;
  }
  let currentUpn: string | null = startNode.managerUpn;
  let hops = 0;
  while (currentUpn !== null && hops < MANAGER_CHAIN_CAP) {
    if (visited.has(currentUpn)) {
      onCycleDetected();
      return;
    }
    const node = index.byUpn.get(currentUpn);
    if (node === undefined) {
      return;
    }
    visited.add(currentUpn);
    collected.add(currentUpn);
    currentUpn = node.managerUpn;
    hops += 1;
  }
  if (currentUpn !== null && hops >= MANAGER_CHAIN_CAP) {
    onCycleDetected();
  }
}

/**
 * BFS over the reverse adjacency map starting from `startUpn`, collecting
 * every descendant. Bounded by node count so a cycle in the data cannot
 * loop forever.
 */
function walkDescendants(
  startUpn: string,
  index: NodeIndex,
  collected: Set<string>,
): void {
  const queue: string[] = [startUpn];
  while (queue.length > 0) {
    const upn = queue.shift() as string;
    const reports = index.reportsByManagerUpn.get(upn);
    if (reports === undefined) {
      continue;
    }
    for (const report of reports) {
      if (collected.has(report.id)) {
        continue;
      }
      collected.add(report.id);
      queue.push(report.id);
    }
  }
}

function filterEdges(edges: IOrgGraphEdge[], kept: Set<string>): IOrgGraphEdge[] {
  return edges.filter((edge) => kept.has(edge.source) && kept.has(edge.target));
}

function computeMyTeam(
  currentUserUpn: string,
  index: NodeIndex,
): Set<string> {
  const collected = new Set<string>();
  let warned = false;
  const warnOnce = (): void => {
    if (warned) {
      return;
    }
    warned = true;
    console.warn(
      `useGraphFilter: manager-chain cycle or cap reached while walking from ${currentUserUpn}`,
    );
  };

  // If the start node is missing from the graph the result is empty;
  // US6 will render that as the empty state.
  if (index.byUpn.has(currentUserUpn)) {
    collected.add(currentUserUpn);
    walkAncestors(currentUserUpn, index, collected, warnOnce);
    walkDescendants(currentUserUpn, index, collected);
  }
  return collected;
}

function applyFilter(graph: IOrgGraphResponse, params: FilterParams): FilteredGraph {
  if (params.mode === 'all') {
    return {
      nodes: graph.nodes,
      edges: graph.edges,
      departments: graph.departments,
      fallbackReason: null,
    };
  }

  if (params.mode === 'my-team') {
    if (params.currentUserUpn === null) {
      return {
        nodes: graph.nodes,
        edges: graph.edges,
        departments: graph.departments,
        fallbackReason: 'no-current-user',
      };
    }
    const index = buildIndex(graph.nodes);
    const kept = computeMyTeam(params.currentUserUpn, index);
    const nodes = graph.nodes.filter((node) => kept.has(node.id));
    return {
      nodes,
      edges: filterEdges(graph.edges, kept),
      departments: graph.departments,
      fallbackReason: null,
    };
  }

  // mode === 'department'
  const kept = new Set<string>();
  const nodes: IOrgGraphNode[] = [];
  for (const node of graph.nodes) {
    if (node.departmentId === params.departmentId) {
      kept.add(node.id);
      nodes.push(node);
    }
  }
  return {
    nodes,
    edges: filterEdges(graph.edges, kept),
    departments: graph.departments,
    fallbackReason: null,
  };
}

/** Stable cache key for the params object so useMemo can compare by value. */
function paramsKey(params: FilterParams): string {
  if (params.mode === 'all') {
    return 'all';
  }
  if (params.mode === 'my-team') {
    // Distinguish `null` (fallback to full graph) from `''` (no-match empty
    // result) so the memoised value is not reused across them.
    return params.currentUserUpn === null
      ? 'my-team:__null__'
      : `my-team:${params.currentUserUpn}`;
  }
  return `department:${params.departmentId}`;
}

export function useGraphFilter(
  graph: IOrgGraphResponse,
  params: FilterParams,
): FilteredGraph {
  const key = paramsKey(params);
  // We depend on `graph` by reference and on `params` by stable string key.
  // Callers that want full referential stability across renders should hold
  // both inputs stable themselves.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => applyFilter(graph, params), [graph, key]);
}
